module Applications

open System
open Fable.React
open Dotyk.Portal.Model
open Elmish
open Fulma
open Fable.React.Props
open ListView

type OpenedView =
| App
| Access

type LangsValue = {
    value: string
}

type StateLang<'t> =
| LoadingLang
| Langs of LangsValue
| ErrorLang of exn

type MsgLang<'req, 't>=
| LoadedLang of 'req * string
| ErrorLang of 'req * exn

let getLangsValue load locId=
    LoadingLang,
    Cmd.OfPromise.either
        load
        locId
        (fun r -> LoadedLang(locId, r))
        (fun e -> ErrorLang(locId, e))

type LocationView = {
    locations : LocationInfo State
    apps : ApplicationInfo State option
    langs : LangsValue StateLang option
}

type State = {
    locations : LocationView
    access : AccessInfo option
    openedView : OpenedView
    iamButton : IamButton.State option
}

type AccessMsg =
| Loaded of AccessInfo*ApplicationInfo
| Removed of UserAccess*ApplicationInfo
| Added of UserAccess*ApplicationInfo
| Error of string*ApplicationInfo

type LocationListMsg =
| Locations of Msg<unit, LocationInfo>
| Applications of Msg<string, ApplicationInfo>
| Languages of  MsgLang<string, string>

type Msg =
| AppList of LocationListMsg
| Access of AccessMsg
| SetView of OpenedView
| IamButton of IamButton.Msg

let private getApp =
    function
    | Some (List ({ selected = Some (app) })) -> Some app
    | _ -> None

let private getSelectedApp model =
    match model.locations with
    | { LocationView.apps = (Some (List {SelectedList.selected = s })) } -> s
    | _ -> None
    
let getLanguages model =
    match model.locations with
    | { LocationView.langs = (Some (Langs {value = s})) } -> s
    | _ -> ""

let private removeAccess (app:ApplicationInfo) acc =
    Cmd.OfPromise.either
        (Service.removeAccess app.url)
        (acc)
        (fun i -> (i, app) |> AccessMsg.Loaded |> Access )
        (fun i -> (i.ToString(), app) |> AccessMsg.Error |> Access)

let private addAccess (app:ApplicationInfo) acc =
    Cmd.OfPromise.either
        (Service.addAccess app.url)
        (acc)
        (fun i -> (i, app) |> AccessMsg.Loaded |> Access )
        (fun i -> (i.ToString(), app) |> AccessMsg.Error |> Access)

let private loadSubs app () =
    Service.getAvailableSubs (app.url)
        
let private connectIam app sub =
    Service.connectIam (app.url) (sub.id)

let private initLocations () =
    let (m,c) =
        ListView.init
            Service.getLocations
            ()
    { locations = m; apps = None; langs = None },
    (Cmd.map Locations c)

let private initIamButton app model =
    let (btn, btnMsg ) = IamButton.init (loadSubs app) app.iam;
    { model with iamButton = Some btn; openedView = OpenedView.App }, Cmd.map IamButton btnMsg

let init () =
    let (lm, lc) = initLocations ()

    { locations = lm
      access = None
      openedView = OpenedView.App
      iamButton = None
    }, Cmd.batch [
        Cmd.map AppList lc
    ]

let updateLangs msg model  =
    match msg, model with
    | LoadedLang (req, value), _ ->
        Langs {value = value}, Cmd.Empty
    | ErrorLang (arg, e), _ ->
        StateLang.ErrorLang e, Cmd.Empty

let private updateAppList msg (model:LocationView) =
    match msg with
    | Locations l ->
        let (model, ic) =
            match l with
            | Selected s ->
                let (m,c) = ListView.init Service.getApps s.id
                let (mlangs,cLangs) = getLangsValue Service.getLangs s.id
                { model with apps = Some m; langs = Some mlangs }, Cmd.batch [(Cmd.map Applications c); Cmd.map Languages cLangs]
            | _ -> (model, Cmd.Empty)

        let (m,c) = ListView.update (fun _ -> true) true l model.locations
        { model with locations = m }, Cmd.batch [(Cmd.map Locations c); ic]

    | Applications a ->
        match model.apps with
        | Some ap -> 
            let (m,c) =
                ListView.update
                    (fun arg ->
                        match model.locations with
                        | List { selected = Some ( { id = location }) } -> arg = location
                        | _ -> false)
                    true a ap
            
            { model with apps = Some m }, (Cmd.map Applications c)
        | None -> model, Cmd.Empty

    | Languages langs ->
        match model.langs with
        | Some lang ->
            let (m,c) = updateLangs langs lang
            {model with langs = Some m}, (Cmd.map Languages c)
        | None -> model, Cmd.Empty

let private updateAccess msg model selectedApp =
    match msg with
    | Added (a, app) ->
        match selectedApp with
        | Some selApp when app = selApp -> model, addAccess app a
        | _ -> model, Cmd.Empty
    | Error _ -> model, Cmd.Empty
    | Loaded (a,app) ->
        match selectedApp with
        | Some selApp when app = selApp -> (Some a), Cmd.Empty
        | _ -> model, Cmd.Empty
    | Removed (a, app) ->
        match selectedApp with
        | Some selApp when app = selApp -> model, removeAccess app a
        | _ -> model, Cmd.Empty

let private updateAppInList (list : State<ApplicationInfo>) (app:ApplicationInfo) =
    match list with
    | List list ->
        let newList = (list.items |> List.map (fun i -> if i.url = app.url then app else i))
        List {
            list
                with
                    items = newList
                    selected =
                        if list.selected.IsSome && list.selected.Value.url = app.url
                        then Some app
                        else list.selected
        }
    | _ -> list 

let update msg model =
    match msg with
    | SetView v -> { model with openedView = v }, Cmd.Empty
    | AppList msg ->
        let (m, c) = updateAppList msg model.locations

        let (model, cmd) =
            match (getApp model.locations.apps), (getApp m.apps) with
            | None, Some app -> initIamButton app model
            | Some app1, Some app2 when not (app1.url = app2.url) -> { model with openedView = OpenedView.App } |> initIamButton app2
            | _, None -> { model with iamButton = None }, Cmd.Empty
            | _ -> model, Cmd.Empty

        { model with locations = m }, Cmd.batch [(Cmd.map AppList c); cmd]
    | Access msg -> 
        let (m, c) = updateAccess msg model.access (getSelectedApp model)
        { model with access = m}, c

    | IamButton b ->
        match model.iamButton, model.locations.apps with
        | Some cm, Some (List { selected = Some (app) }) when app.iam.IsSome ->
            let (m,c) = IamButton.update (connectIam app) b (cm)

            let newLocations =
                match b with
                | IamButton.Msg.Connected id ->
                    let newApp = { app with iam = Some { app.iam.Value with id = Some id } };
                    let newList = updateAppInList (model.locations.apps.Value) newApp;

                    { model.locations with apps = Some newList }
                | _ -> model.locations

            { model with iamButton = Some m; locations = newLocations }, Cmd.map IamButton c
        | _, _ -> model, Cmd.Empty

let private progress =
    div
        [ Class "v-align" ]
        [ Fulma.Progress.progress [ Progress.Size (Fulma.Size.IsSmall); ] [] ]

let private locationsList (locations : LocationInfo State) dispatch =
    match locations with
    | Loading -> progress
    | State.Error e -> str (e.ToString());
    | State.List list ->
        match list.selected with
        |Some selectedLocation ->
            let hash = Browser.Dom.window.location.hash
            match Some 
                (if hash.Contains("_")
                 then hash.[(hash.IndexOf('/') + 1)..(hash.IndexOf('_') - 1)]
                 else hash.[(hash.IndexOf('/') + 1)..]) with
            | Some l ->
                match list.items |> List.tryFind (fun i -> i.id = l) with
                | Some e ->
                    if e.id <> selectedLocation.id
                    then Browser.Dom.window.location.hash <- "#apps/" + selectedLocation.id
                    else Browser.Dom.window.location.hash <- "#apps/" + selectedLocation.id + (if hash.Contains("_") then hash.[hash.IndexOf('_')..] else "")
                    dropDownList {list with selected = Some selectedLocation} (str "Select location") (Selected >> Locations >> dispatch) (fun i-> str i.name)
                | None _ ->
                    Browser.Dom.window.location.hash <- "#apps/" + selectedLocation.id
                    selectedLocation |> (Selected >> Locations >> dispatch)
                    dropDownList list (str "Select location") (Selected >> Locations >> dispatch) (fun i-> str i.name)
            | None _ ->
                Browser.Dom.window.location.hash <- "#apps/" + selectedLocation.id
                selectedLocation |> (Selected >> Locations >> dispatch)
                dropDownList list (str "Select location") (Selected >> Locations >> dispatch) (fun i-> str i.name)
        | None _ ->
            let hash = Browser.Dom.window.location.hash
            match Some
                (if hash.Contains("_")
                 then hash.[(hash.IndexOf('/') + 1)..(hash.IndexOf('_') - 1)]
                 else hash.[(hash.IndexOf('/') + 1)..(hash.Length - 1)]) with
            | Some l ->
                match list.items |> List.tryFind (fun i -> i.id = l) with
                | Some e ->
                    e |> (Selected >> Locations >> dispatch)
                    dropDownList {list with selected = Some e} (str "Select location") (Selected >> Locations >> dispatch) (fun i-> str i.name)
                | None _ -> dropDownList list (str "Select location") (Selected >> Locations >> dispatch) (fun i-> str i.name)
            | None _ ->
                dropDownList list (str "Select location") (Selected >> Locations >> dispatch) (fun i-> str i.name)
            

let private displayAppListItem app =
    div [ ClassName "applistitem" ] [
      match app.icon with
      | Some icon -> img [ ClassName "applistitem_img"; Src icon.url; Style [ BackgroundColor icon.backgroundColor ] ]
      | None -> div [ ClassName "applistitem_img" ] []

      span [ ClassName "applistitem_name" ] [ str app.name ]
    ]

let private appsList (state : State) dispatch =
    match state.locations.apps with
    | None -> div [ ClassName "spaced-text" ] [str "Select location to load applications"]
    | Some (Loading) -> progress
    | Some (State.Error e) -> str (e.ToString())
    | Some (State.List { items = [] }) -> div [ ClassName "spaced-text" ] [str "There are no applications with 'Edit' access in this location"]
    | Some (State.List list) ->
        match list.selected with
        |Some selected ->
            match state.locations.locations with
            | List l ->
                match l.selected with
                | Some selectedLocation ->
                    Browser.Dom.window.location.hash <- "#apps/" + selectedLocation.id + "_" + (Uri.EscapeDataString selected.url)
                | None _ -> Browser.Dom.window.location.hash <- "#apps/" + (Uri.EscapeDataString selected.url)
            | State.Loading -> Browser.Dom.window.location.hash <- "#apps/" + (Uri.EscapeDataString selected.url)
            | State.Error _ -> Browser.Dom.window.location.hash <- "#apps/" + (Uri.EscapeDataString selected.url)
            dropDownList list (str "Select application") (Selected >> Applications >> dispatch) displayAppListItem
        | None _ ->
            match Some (Uri.UnescapeDataString(Browser.Dom.window.location.hash.[(Browser.Dom.window.location.hash.IndexOf('_') + 1)..])) with
            | Some l ->
                match list.items |> List.tryFind (fun i -> i.url = l) with
                | Some e ->
                    e |> (Selected >> Applications >> dispatch)
                    dropDownList {list with selected = Some e} (str "Select application") (Selected >> Applications >> dispatch) displayAppListItem
                | None _ -> dropDownList list (str "Select application") (Selected >> Applications >> dispatch) displayAppListItem
            | None _ ->
                dropDownList list (str "Select application") (Selected >> Applications >> dispatch) displayAppListItem

let private accessModal onClose (className, content) =
    let customClass =
        match className with
        | Some c -> [ Modal.CustomClass c ]
        | _ -> []

    Modal.modal
        ( (Modal.IsActive true)::customClass )
        [ Modal.background [ Props [ OnClick (fun _ -> onClose())] ] []
          Modal.content [] [ Box.box' [] [ content ]] ]


let private accessEditor iamUrl onClose app langs =
    IamButton.editorView iamUrl app langs
    |> accessModal onClose

let private frameView iamUrl state dispatch =

    match getSelectedApp state with
    | None -> nothing
    | Some a ->
        let accessModal = 
            match state.openedView with
            | OpenedView.Access -> accessEditor iamUrl (fun () -> OpenedView.App |> SetView |> dispatch) a (getLanguages state)
            | OpenedView.App -> nothing

        let isHttpOverHttps = Browser.Dom.window.location.protocol = "https:" && a.cmsUrl.StartsWith("http:")
        
        div [ Class "stretch-height iframecontainer" ]
            [
                AppHost.editor
                    a.url
                    a.cmsUrl
                    isHttpOverHttps
                    (getLanguages state)

                accessModal
            ]

let filterFirstUnselected test list =
    list |> List.takeWhile test

let view iamUrl state dispatch =
    let narrowColumn i = Column.column [ Column.Width(Screen.All, Column.IsNarrow)
                                         Column.CustomClass "is-spaced1" ] [ i ]

    let accessEditor =
        match state.iamButton with
        | Some iam -> IamButton.view iam (IamButton >> dispatch) (fun () -> OpenedView.Access |> SetView |> dispatch)
        | None -> nothing

    let tabs =
        [ locationsList state.locations.locations (AppList >> dispatch)
          appsList state (AppList >> dispatch)
          accessEditor ]

    div [ Class "applications" ]
        [ Columns.columns
            [ Columns.CustomClass "locations-menu"
              Columns.IsMobile
              Columns.IsMultiline
              Columns.IsVCentered]
            (tabs |> List.map narrowColumn)

          frameView iamUrl state dispatch
        ]
